import type React from "react";
import { useRef, useState } from "react";
import { UploadCloud, Users } from "lucide-react";
import type { appUser } from "@duress/shared";
import { fmtInt } from "@/lib/format";
import { BRAND } from "@/lib/colors";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { BreakdownBars, type BreakdownBar } from "../dashboard/BreakdownBars";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useAppUsers, useUploadAppUsers } from "../useAppUsers";

// Same drag-and-drop pattern as UploadPeriodDialog's dropzone, simplified to
// a single file -- the roster upload has no multi-slot assignment step, so
// there's nothing there worth sharing a component with yet.
function AppUserDropzone({
  onUpload,
  isUploading,
  compact = false,
}: {
  onUpload: (file: File) => void;
  isUploading: boolean;
  compact?: boolean;
}): React.JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList): void => {
    const file = files[0];
    if (file) onUpload(file);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Drop the app user roster CSV here, or click to browse"
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => {
        setIsDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
      }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed text-center transition-colors ${
        compact ? "p-4" : "p-6"
      } ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}`}
    >
      <UploadCloud
        className={compact ? "h-5 w-5 text-muted-foreground" : "h-8 w-8 text-muted-foreground"}
        aria-hidden="true"
      />
      <p className="text-sm font-medium">
        {isUploading ? "Uploading…" : "Drag the App User CSV here, or click to browse"}
      </p>
      {!compact && (
        <p className="text-xs text-muted-foreground">The "All App Users" export — .csv</p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        disabled={isUploading}
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ── Pure presentational component ────────────────────────────────────────────

function buildTiles(d: appUser.AppUserData): KPITile[] {
  return [
    { label: "Registered Users", value: fmtInt(d.total_users) },
    {
      // "App" is load-bearing in the label, not just "Adoption Rate" -- on a
      // mixed client (devices + app users) this tab sits alongside Executive
      // Summary's device-derived "Network Adoption" tile, and the two must
      // never read as the same number.
      label: "App Adoption Rate",
      value: d.adoption_rate === null ? "—" : `${d.adoption_rate.toFixed(1)}%`,
      sub:
        d.adoption_rate === null
          ? "no check-in/emergency/alert data uploaded yet"
          : "did check-in, emergency, or team-alert activity ÷ registered",
    },
    { label: "Invited (Pending)", value: fmtInt(d.invited_users) },
    { label: "Expired Invitations", value: fmtInt(d.expired_users) },
  ];
}

const TEAM_COLUMNS: DataTableColumn<appUser.AppUserTeamEntry>[] = [
  { key: "team", label: "Team", render: (v) => <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span> },
  { key: "total", label: "Total", render: (v) => <span className="num">{fmtInt(Number(v))}</span> },
  { key: "active", label: "Active", render: (v) => <span className="num">{fmtInt(Number(v))}</span> },
  { key: "invited", label: "Invited", render: (v) => <span className="num">{fmtInt(Number(v))}</span> },
  { key: "expired", label: "Expired", render: (v) => <span className="num">{fmtInt(Number(v))}</span> },
];

export function AppUsage({
  data,
  onUpload,
  isUploading,
}: {
  data: appUser.AppUserData;
  onUpload: (file: File) => void;
  isUploading: boolean;
}): React.JSX.Element {
  if (data.total_users === 0) {
    return (
      <div data-app-usage-state="not-uploaded" className="space-y-4">
        <EmptyState
          icon={Users}
          body="No app user roster uploaded yet for this client. Upload the 'All App Users' export to see registration, adoption, and team breakdowns."
        />
        <div className="max-w-md mx-auto">
          <AppUserDropzone onUpload={onUpload} isUploading={isUploading} />
        </div>
      </div>
    );
  }

  const statusBars: BreakdownBar[] = [
    { label: "Active", display: fmtInt(data.active_users), color: BRAND.green, pct: (data.active_users / data.total_users) * 100 },
    { label: "Invited", display: fmtInt(data.invited_users), color: BRAND.blue, pct: (data.invited_users / data.total_users) * 100 },
    { label: "Expired", display: fmtInt(data.expired_users), color: BRAND.red, pct: (data.expired_users / data.total_users) * 100 },
  ];

  const tiles = buildTiles(data);

  return (
    <div className="space-y-6">
      <KPIGrid tiles={tiles} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
        <div className="card-duress p-5" data-app-usage-panel="status">
          <p className="font-head font-semibold text-sm text-foreground mb-4">
            Registration Status
          </p>
          <BreakdownBars bars={statusBars} />
        </div>

        <div className="card-duress p-5 space-y-3">
          <p className="font-head font-semibold text-sm text-foreground">Re-upload Roster</p>
          <AppUserDropzone onUpload={onUpload} isUploading={isUploading} compact />
          <p className="text-sm text-muted-foreground">
            {data.uploaded_at
              ? `Last uploaded ${new Date(data.uploaded_at).toLocaleDateString()}. Each upload replaces the prior roster — this is a snapshot of who's registered right now, not a period-scoped history.`
              : "Snapshot of who's registered right now — not a period-scoped history."}
          </p>
        </div>
      </div>

      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">Users by Team</p>
        {data.by_team.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No team data — the roster export is missing a Team column.
            </p>
          </div>
        ) : (
          <DataTable<appUser.AppUserTeamEntry>
            columns={TEAM_COLUMNS}
            data={data.by_team}
            defaultSort={{ key: "total", dir: "desc" }}
            searchKeys={["team"]}
          />
        )}
      </div>

      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          Built from the App User roster export, not device/session data — if this client also
          has Falcon/Eagle devices, that fleet is unaffected and still drives Executive
          Summary's Network Adoption tile and the Health Score. <strong>App Adoption Rate</strong>{" "}
          here is the share of registered app users who appear in at least one check-in,
          emergency, or team-alert record across any uploaded period — not the roster's own
          Status column. A user can be marked Active there (they accepted an invite, or their
          session simply hasn't expired) without ever having actually used the app. This tab
          also deliberately ignores the export's "Last Signed In" column for the same reason: a
          mobile app session persists indefinitely, so login recency doesn't indicate actual
          usage.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function AppUsageTab({ clientId }: { clientId: string }): React.JSX.Element {
  const { data, isLoading, error, refetch } = useAppUsers(clientId);
  const upload = useUploadAppUsers(clientId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load app user roster." onRetry={refetch} />;
  }

  return (
    <AppUsage
      data={data}
      onUpload={(file) => {
        void upload.mutateAsync(file);
      }}
      isUploading={upload.isPending}
    />
  );
}
